/* ============================================================================
 * useUIStateOperations.ts - Instrumented UI state operations
 * ============================================================================ */

import { useCallback, type Dispatch, type SetStateAction } from 'react';
import type { ViewFilter } from '@/shared/types/projectData';

interface UseUIStateOperationsParams {
  // Current state
  filters: ViewFilter;
  searchQuery: string;
  sortBy: string | null;
  isCompactView: boolean;
  
  // State setters
  setFilters: Dispatch<SetStateAction<ViewFilter>>;
  setSearchQueryState: Dispatch<SetStateAction<string>>;
  setSortByState: Dispatch<SetStateAction<string | null>>;
  setIsCompactViewState: Dispatch<SetStateAction<boolean>>;
  
  // Functions
  captureOperation: (
    type: string,
    category: 'view' | 'selection' | 'filter' | 'navigation' | 'preference' | 'interaction' | 'edit',
    before: unknown,
    after: unknown,
    description?: string,
    component?: string,
    skipHistory?: boolean
  ) => void;
}

/**
 * Hook for instrumented UI state operations
 */
export function useUIStateOperations({
  filters,
  searchQuery,
  sortBy,
  isCompactView,
  setFilters,
  setSearchQueryState,
  setSortByState,
  setIsCompactViewState,
  captureOperation
}: UseUIStateOperationsParams) {
  
  // Instrumented setFilters function
  const instrumentedSetFilters = useCallback((newFilters: ViewFilter) => {
    const before = filters;
    const after = newFilters;
    
    // Capture the filter operation for undo
    const changedFields = Object.keys({ ...before, ...after }).filter(
      key => JSON.stringify(before[key as keyof ViewFilter]) !== JSON.stringify(after[key as keyof ViewFilter])
    );
    
    if (changedFields.length > 0) {
      captureOperation('filter-change', 'filter',
        before,
        after,
        `Update filters: ${changedFields.join(', ')}`,
        'FilterPanel'
      );
    }
    
    setFilters(newFilters);
  }, [filters, setFilters, captureOperation]);
  
  // Instrumented search query setter
  const setSearchQuery = useCallback((query: string) => {
    const before = searchQuery;
    const after = query;
    
    // Only capture if the value actually changed
    if (before !== after) {
      captureOperation('search-change', 'filter',
        { searchQuery: before },
        { searchQuery: after },
        `Search: "${after}"`,
        'SearchInput'
      );
    }
    
    setSearchQueryState(after);
  }, [searchQuery, setSearchQueryState, captureOperation]);
  
  // Instrumented sort setter
  const setSortBy = useCallback((newSortBy: string | null) => {
    const before = sortBy;
    const after = newSortBy;
    
    // Only capture if the value actually changed
    if (before !== after) {
      captureOperation('sort-change', 'filter',
        { sortBy: before },
        { sortBy: after },
        `Sort by: ${after || 'default'}`,
        'SortDropdown'
      );
    }
    
    setSortByState(after);
  }, [sortBy, setSortByState, captureOperation]);
  
  // Instrumented compact view setter
  const setIsCompactView = useCallback((isCompact: boolean) => {
    const before = isCompactView;
    const after = isCompact;
    
    // Only capture if the value actually changed
    if (before !== after) {
      captureOperation('view-mode-change', 'preference',
        { isCompactView: before },
        { isCompactView: after },
        `${after ? 'Enable' : 'Disable'} compact view`,
        'ViewToggle'
      );
    }
    
    setIsCompactViewState(after);
  }, [isCompactView, setIsCompactViewState, captureOperation]);
  
  return {
    instrumentedSetFilters,
    setSearchQuery,
    setSortBy,
    setIsCompactView
  };
}